import { useQuery } from 'data-fetching-client';
import { useCallback } from 'react';
import { FEATURES_DEFAULTS } from 'pricing-pages-api/EarlyRequesterNames';
import { GET_FEATURES_BY_API_NAMES, GET_FEATURES_WITH_DEFAULTS } from 'pricing-pages-api/features/queries';
import { getI18nLangLocale } from 'pricing-pages-lib/utils/languageUtils';
import { PinnedFeaturesConfig } from '../../constants/PinnedFeaturesConfig';
import { pinKeyFeatures } from '../../utils/featureUtils';
import { logApiError, logApiSuccess } from '../../utils/apiLogging';
import { useIsUngatedForM1HubPages } from '../gating/useIsUngated';
export const useFeaturesForApiNames = highlightedProducts => {
  const i18nLangLocale = getI18nLangLocale();
  const isM1HubPages = useIsUngatedForM1HubPages();
  const version = isM1HubPages ? 'v2' : 'v1';
  const handleCompleted = useCallback(() => {
    logApiSuccess({
      queryName: 'GET_FEATURES_BY_API_NAMES',
      operation: 'getFeaturesByApiNames',
      version
    });
  }, [version]);
  const handleError = useCallback(error => {
    logApiError(error, {
      queryName: 'GET_FEATURES_BY_API_NAMES',
      operation: 'getFeaturesByApiNames',
      version
    });
  }, [version]);
  const {
    data,
    loading,
    error
  } = useQuery(GET_FEATURES_BY_API_NAMES, {
    variables: {
      productApiNames: highlightedProducts,
      i18nLangLocale
    },
    skip: !(highlightedProducts !== null && highlightedProducts !== void 0 && highlightedProducts.length),
    onCompleted: handleCompleted,
    onError: handleError
  });
  return {
    features: (data === null || data === void 0 ? void 0 : data.features) || [],
    loading,
    error
  };
};

/**
 * Hook to fetch features for multiple product lines using the feature store service
 *
 * @param productLines - Array of product lines that will be converted to a ProductLine <> ApiName map using getHighlightedProductsForSkuCategory
 * @param productLineToApiName - A direct map of product lines to associated API names
 *
 * The hook accepts either productLines (which are converted to API names) or a direct productLineToApiName map.
 * - Product lines are mapped to SKU categories using bundleTypeOrProductLineToSkuCategoryName
 * - SKU categories are then mapped to highlighted products using getHighlightedProductsForSkuCategory
 * - The resulting API names are used to fetch features from the feature store
 *
 * @returns Object containing featuresByProductLine map, loading state, and error state
 */
export const useFeaturesBySkuCategoryName = ({
  skuCategoryNameToApiName
}) => {
  // Query for Marketing
  const marketingData = useFeaturesForApiNames(skuCategoryNameToApiName['MARKETING_PRODUCTS']);

  // Query for Sales
  const salesData = useFeaturesForApiNames(skuCategoryNameToApiName['SALES_PRODUCTS']);

  // Query for Service
  const serviceData = useFeaturesForApiNames(skuCategoryNameToApiName['SERVICE_HUB_PRODUCTS']);

  // Query for CMS
  const cmsData = useFeaturesForApiNames(skuCategoryNameToApiName['CMS_HUB_PRODUCTS']);

  // Query for Commerce
  const commerceData = useFeaturesForApiNames(skuCategoryNameToApiName['COMMERCE_PRODUCTS']);

  // Query for Operations
  const operationsData = useFeaturesForApiNames(skuCategoryNameToApiName['OPERATIONS_HUB_PRODUCTS']);
  const featuresBySkuCategoryName = Object.keys(skuCategoryNameToApiName).reduce((acc, skuCategoryName) => {
    switch (skuCategoryName) {
      case 'MARKETING_PRODUCTS':
        acc['MARKETING_PRODUCTS'] = marketingData.features;
        break;
      case 'SALES_PRODUCTS':
        acc['SALES_PRODUCTS'] = salesData.features;
        break;
      case 'SERVICE_HUB_PRODUCTS':
        acc['SERVICE_HUB_PRODUCTS'] = serviceData.features;
        break;
      case 'CMS_HUB_PRODUCTS':
        acc['CMS_HUB_PRODUCTS'] = cmsData.features;
        break;
      case 'OPERATIONS_HUB_PRODUCTS':
        acc['OPERATIONS_HUB_PRODUCTS'] = operationsData.features;
        break;
      case 'COMMERCE_PRODUCTS':
        acc['COMMERCE_PRODUCTS'] = commerceData.features;
        break;
      default:
        acc[skuCategoryName] = [];
        break;
    }
    return acc;
  }, {});
  const loading = marketingData.loading || salesData.loading || serviceData.loading || cmsData.loading || operationsData.loading;
  return {
    featuresBySkuCategoryName,
    loading,
    error: marketingData.error || salesData.error || serviceData.error || cmsData.error || operationsData.error
  };
};
export const useFeaturesWithDefaults = () => {
  const i18nLangLocale = getI18nLangLocale();
  const isM1HubPages = useIsUngatedForM1HubPages();
  const version = isM1HubPages ? 'v2' : 'v1';
  const handleCompleted = useCallback(() => {
    logApiSuccess({
      queryName: 'GET_FEATURES_WITH_DEFAULTS',
      operation: 'getFeaturesWithDefaults',
      version
    });
  }, [version]);
  const handleError = useCallback(error => {
    logApiError(error, {
      queryName: 'GET_FEATURES_WITH_DEFAULTS',
      operation: 'getFeaturesWithDefaults',
      version
    });
  }, [version]);
  const {
    data,
    loading,
    error
  } = useQuery(GET_FEATURES_WITH_DEFAULTS, {
    variables: {
      i18nLangLocale,
      earlyRequestName: FEATURES_DEFAULTS
    },
    onCompleted: handleCompleted,
    onError: handleError
  });
  return {
    features: (data === null || data === void 0 ? void 0 : data.featuresWithDefaults) || {},
    loading,
    error
  };
};

/**
 * Hook to fetch features for product lines and return them with pinned features prioritized
 * Uses pinKeyFeatures utility to organize features according to PinnedFeaturesConfig
 *
 * @param productLineToApiName - Map of product lines to associated API names
 * @returns Object containing pinned featuresByProductLine map, loading state, and error state
 */
export const usePinnedFeaturesBySkuCategoryName = skuCategoryNameToApiName => {
  const {
    featuresBySkuCategoryName,
    loading,
    error
  } = useFeaturesBySkuCategoryName({
    skuCategoryNameToApiName
  });
  const pinnedConfig = PinnedFeaturesConfig;
  const pinnedFeaturesBySkuCategoryName = Object.keys(featuresBySkuCategoryName).reduce((acc, skuCategoryName) => {
    var _featuresBySkuCategor;
    acc[skuCategoryName] = pinKeyFeatures((_featuresBySkuCategor = featuresBySkuCategoryName[skuCategoryName]) !== null && _featuresBySkuCategor !== void 0 ? _featuresBySkuCategor : [], skuCategoryName, pinnedConfig);
    return acc;
  }, {});
  return {
    featuresBySkuCategoryName: pinnedFeaturesBySkuCategoryName,
    loading,
    error
  };
};